import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { isNotEmpty, useForm } from "@mantine/form";
import {
  TextInput,
  Button,
  Stack,
  Center,
  Loader,
  Paper,
  Accordion,
  Text,
  Group,
  List,
  ThemeIcon,
  rem,
  Tooltip,
  MultiSelect,
  Badge,
  NumberInput,
  Textarea,
  Switch,
} from "@mantine/core";
import { randomId, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconTrash,
  IconDeviceFloppy,
  IconCircleXFilled,
  IconLink,
} from "@tabler/icons-react";
import { deleteVersion, updateVersion, getModelRouting } from "../api";
import { notifications } from "@mantine/notifications";
import PromptEditor from "../_Common/PromptEditor";
import { validateTemplateTokens } from "../_Common/templateValidation";
import useToolStore from "../Tools/toolStore";
import ModelSelect from "../_Common/ModelSelect";
import ModelMultiSelect from "../_Common/ModelMultiSelect";
import ReasoningEffortSelect from "../_Common/ReasoningEffortSelect";
import useFlowStore from "./flowStore";
import AgentToolsPreview from "./AgentToolsPreview";
import LinkAgentModal from "./LinkAgentModal";

// Mantine NumberInput reports a cleared field as "". System.Text.Json cannot
// read that as float?, so a cleared temperature has to leave as null — otherwise
// a value, once set, can never be removed.
function numberOrNull(value) {
  if (value === "" || value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === ".") return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const FlowVersionEditor = forwardRef(({ flow, parentFlow }, ref) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);
  const [linkAgentModalOpened, linkAgentModal] = useDisclosure(false);
  const [linkAgentTargetIndex, setLinkAgentTargetIndex] = useState(null);
  const { flowTemplates, setFlowTemplates, setSelectedVersion } =
    useFlowStore();
  const { fetchTools, tools } = useToolStore();

  // Fetch global routing rules for display in model selector
  useEffect(() => {
    getModelRouting()
      .then(setRoutingRules)
      .catch((err) => {
        console.error("Failed to load routing rules:", err);
        setRoutingRules([]);
      });
  }, []);

  useEffect(() => {
    if (tools?.length === 0) {
      fetchTools();
    }
  }, [fetchTools, tools?.length]);

  const saveVersion = () => {
    return new Promise((resolve, reject) => {
      const validation = form.validate();
      if (validation.hasErrors) {
        handleError(validation.errors);
        reject(new Error("Validation failed"));
        return;
      }
      const values = form.getValues();
      const payload = {
        ...values,
        templates: values.templates?.map((template) => ({
          ...template,
          temperature: numberOrNull(template.temperature),
          maxToolCalls: numberOrNull(template.maxToolCalls),
        })),
      };
      updateVersion(flow.flowId, flow.id, payload)
        .then((data) => {
          var otherVersions = flowTemplates.filter((q) => q.id != data.id);
          setFlowTemplates([data, ...otherVersions]);
          setFormErrors([]);
          form.resetDirty();
          notifications.show({
            title: "Saved successfully",
            color: "green",
          });
          resolve(data);
        })
        .catch(reject);
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      isDirty() {
        return form.isDirty();
      },
      save() {
        return saveVersion();
      },
    }),
    // `form` (Mantine) and `saveVersion` are recreated every render; including them would
    // rebuild the imperative handle every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flow, flowTemplates]
  );

  const form = useForm({
    // mode: "uncontrolled",
    validateInputOnBlur: true,
    validate: {
      templates: {
        name: (val) =>
          /^[a-z0-9_]{2,49}[a-z0-9]$/.test(val)
            ? null
            : "Name must only contain lowercase letters, digits, and underscores, and be between 3 and 50 characters long",
        template: (val) =>
          isNotEmpty("Template cannot be empty")(val) ||
          validateTemplateTokens(val),
        userTemplate: (val) => validateTemplateTokens(val),
        responseSchema: (val) =>
          val && typeof val === "string" ? "Invalid JSON" : null,
      },
    },
  });

  useEffect(() => {
    if (flow) {
      // Auto-create a "main" template for new versions with no templates
      if (flow.isEditable && (!flow.templates || flow.templates.length === 0)) {
        const withDefault = {
          ...flow,
          // verifyProgress is explicit so the Switch starts controlled — undefined would make
          // React treat it as uncontrolled and warn the moment it is toggled.
          templates: [
            { key: randomId(), name: "main", template: "", verifyProgress: false },
          ],
        };
        form.setValues(withDefault);
        form.resetDirty(withDefault);
      } else {
        form.setValues(flow);
        form.resetDirty(flow);
      }
    }
    // Mantine form object has unstable identity; including it would rerun this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow]);

  const supportedTools =
    tools?.map((p) => ({ label: p.name, value: p.id })) ?? [];

  const openDeleteModal = (template, index) =>
    modals.openConfirmModal({
      title: `Delete template '${template.name}'`,
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this template? This action cannot be
          undone.
        </Text>
      ),
      labels: { confirm: "Delete template", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => form.removeListItem("templates", index),
    });

  const renderTemplateFields = (item, index, { showDelete = true } = {}) => (
    <Stack mt="xs">
      <TextInput
        description="A machine-readable name. Must not contain any whitespace or special characters."
        placeholder="prefix"
        label="Name"
        disabled={!flow.isEditable}
        withAsterisk
        style={{ flex: 1 }}
        key={form.key(`templates.${index}.name`)}
        {...form.getInputProps(`templates.${index}.name`)}
      />

      <Textarea
        label="Description"
        description="What this template does. Used by the classifier in Route mode."
        autosize
        minRows={1}
        maxRows={3}
        placeholder="Analyzes code for bugs and security issues"
        disabled={!flow.isEditable}
        key={form.key(`templates.${index}.description`)}
        {...form.getInputProps(`templates.${index}.description`)}
      />

      <PromptEditor
        withAsterisk
        disabled={!flow.isEditable}
        label="Template"
        description="The system prompt. You can use [[tokens]] that are either supplied when invoking the template, or reference to another template by name such as [[prefix]]"
        key={form.key(`templates.${index}.template`)}
        {...form.getInputProps(`templates.${index}.template`)}
      />

      <PromptEditor
        disabled={!flow.isEditable}
        label="User Template"
        description="Optional. Rendered as a user message after the system prompt. Supports the same [[tokens]]. Use this to separate static system instructions from dynamic user-specific content for better prompt caching."
        key={form.key(`templates.${index}.userTemplate`)}
        {...form.getInputProps(`templates.${index}.userTemplate`)}
      />

      <Group wrap="nowrap" align="flex-start">
        <ModelSelect
          key={form.key(`templates.${index}.llm`)}
          {...form.getInputProps(`templates.${index}.llm`)}
          description="The model to use when this template is invoked. Sub-templates referenced via [[name]] inherit their caller's model."
          label="LLM"
          disabled={!flow.isEditable}
          routingRules={routingRules}
          style={{ flex: 1 }}
        />

        <NumberInput
          key={form.key(`templates.${index}.temperature`)}
          min={0}
          max={2}
          step={0.01}
          placeholder="Default"
          {...form.getInputProps(`templates.${index}.temperature`)}
          value={form.values.templates?.[index]?.temperature ?? ""}
          onChange={(value) =>
            form.setFieldValue(
              `templates.${index}.temperature`,
              value === "" || value == null ? null : value
            )
          }
          label="Temperature"
          description="Empty = model default"
          disabled={!flow.isEditable}
          w={140}
        />

        <ReasoningEffortSelect
          key={form.key(`templates.${index}.reasoningEffort`)}
          {...form.getInputProps(`templates.${index}.reasoningEffort`)}
          llm={form.getValues().templates?.[index]?.llm}
          disabled={!flow.isEditable}
          w={180}
        />
      </Group>

      <ModelMultiSelect
        description="The fallback LLMs to be used"
        label="Fallbacks"
        disabled={!flow.isEditable}
        searchable
        style={{ flex: 1 }}
        key={form.key(`templates.${index}.fallbacks`)}
        {...form.getInputProps(`templates.${index}.fallbacks`)}
      />

      <Group wrap="nowrap" align="flex-end">
        <MultiSelect
          data={supportedTools || []}
          description="Tools to be used with this template"
          label={
            <>
              Tools{" "}
              <Badge color="red" variant="light" size="xs">
                Preview
              </Badge>
            </>
          }
          disabled={!flow.isEditable}
          searchable
          style={{ flex: 1 }}
          key={form.key(`templates.${index}.toolIds`)}
          {...form.getInputProps(`templates.${index}.toolIds`)}
        />

        <NumberInput
          key={form.key(`templates.${index}.maxToolCalls`)}
          {...form.getInputProps(`templates.${index}.maxToolCalls`)}
          value={form.values.templates?.[index]?.maxToolCalls ?? ""}
          onChange={(value) =>
            form.setFieldValue(
              `templates.${index}.maxToolCalls`,
              value === "" || value == null ? null : value
            )
          }
          label="Max Tool Calls"
          description="Max tool invocation loops"
          disabled={!flow.isEditable}
        />

        {flow.isEditable && (
          <Tooltip label="Create and link a new sub-agent tool">
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconLink size={14} />}
              onClick={() => {
                setLinkAgentTargetIndex(index);
                linkAgentModal.open();
              }}
            >
              Link Sub-Agent
            </Button>
          </Tooltip>
        )}
      </Group>

      <AgentToolsPreview
        selectedToolIds={form.values.templates?.[index]?.toolIds}
        allTools={tools}
        disabled={!flow.isEditable}
        onRemoveTool={(toolId) => {
          const currentToolIds = form.values.templates[index].toolIds || [];
          form.setFieldValue(
            `templates.${index}.toolIds`,
            currentToolIds.filter((id) => id !== toolId)
          );
        }}
      />

      <Textarea
        label="Response Schema"
        description="Optional JSON Schema for structured outputs. The LLM will be constrained to return JSON matching this schema."
        placeholder='{"type": "object", "properties": { ... }, "required": [...]}'
        autosize
        minRows={2}
        maxRows={12}
        disabled={!flow.isEditable}
        key={form.key(`templates.${index}.responseSchema`)}
        value={
          form.values.templates?.[index]?.responseSchema
            ? typeof form.values.templates[index].responseSchema === "string"
              ? form.values.templates[index].responseSchema
              : JSON.stringify(
                  form.values.templates[index].responseSchema,
                  null,
                  2
                )
            : ""
        }
        onChange={(e) => {
          const val = e.currentTarget.value;
          if (!val) {
            form.setFieldValue(`templates.${index}.responseSchema`, null);
            return;
          }
          try {
            form.setFieldValue(
              `templates.${index}.responseSchema`,
              JSON.parse(val)
            );
          } catch {
            // Keep as string while user is typing invalid JSON
            form.setFieldValue(`templates.${index}.responseSchema`, val);
          }
        }}
        error={
          typeof form.values.templates?.[index]?.responseSchema === "string" &&
          form.values.templates[index].responseSchema.length > 0
            ? "Invalid JSON"
            : null
        }
        styles={{ input: { fontFamily: "monospace", fontSize: "0.85rem" } }}
      />

      <Stack gap="xs">
        <Switch
          key={form.key(`templates.${index}.verifyProgress`)}
          {...form.getInputProps(`templates.${index}.verifyProgress`, {
            type: "checkbox",
          })}
          disabled={!flow.isEditable}
          label="Verify progress"
          description="While the run executes, a small verifier model reads the trajectory and grades how far it has got, plus how sure it is. Recorded on the run; it never stops or changes it. Each check is a separate inference and is billed to you."
        />

        {form.values.templates?.[index]?.verifyProgress && (
          <Textarea
            key={form.key(`templates.${index}.verificationCriterion`)}
            {...form.getInputProps(`templates.${index}.verificationCriterion`)}
            label="Verification criterion"
            description="What the verifier judges, in one sentence. A criterion written for this prompt grades noticeably better than the generic fallback."
            placeholder="Did the agent confirm the fix by running a test and reporting its output?"
            autosize
            minRows={2}
            maxRows={4}
            disabled={!flow.isEditable}
          />
        )}
      </Stack>

      {showDelete && flow.isEditable && (
        <Group>
          <Button
            color="red"
            variant="outline"
            onClick={() => openDeleteModal(item, index)}
            leftSection={<IconTrash />}
          >
            Delete '{item.name}'
          </Button>
        </Group>
      )}
    </Stack>
  );

  const templates = form.getValues().templates ?? [];
  const isSingleTemplate = templates.length === 1;

  const fields = templates.map((item, index) => (
    <Accordion.Item key={item.key} value={"template-" + index}>
      <Accordion.Control>{item.name || "Unnamed Template"}</Accordion.Control>
      <Accordion.Panel>
        {renderTemplateFields(item, index)}
      </Accordion.Panel>
    </Accordion.Item>
  ));

  const revealErrorAccordion = (errorKey) => {
    try {
      let templateIndex = parseInt(errorKey.split(".")[1]);
      setSelectedItem("template-" + templateIndex);
    } catch {
      // errorKey did not reference a template index; nothing to reveal.
    }
  };

  const handleError = (errors) => {
    let activeKeys = Object.keys(errors);
    let errorArray = activeKeys.map((key) => ({
      key: key,
      value: errors[key],
    }));

    if (errorArray.length > 0) {
      var firstTemplateError = errorArray.find((error) =>
        error.key.startsWith("templates.")
      );
      if (firstTemplateError) {
        revealErrorAccordion(firstTemplateError.key);
      }

      notifications.show({
        message: `There were ${errorArray.length} validation errors`,
        color: "red",
      });
    }

    setFormErrors(errorArray);
  };

  // Handle adding a newly created agent tool to the current template
  const handleAgentToolCreated = (newTool) => {
    if (linkAgentTargetIndex !== null) {
      const currentToolIds =
        form.values.templates[linkAgentTargetIndex].toolIds || [];
      form.setFieldValue(`templates.${linkAgentTargetIndex}.toolIds`, [
        ...currentToolIds,
        newTool.id,
      ]);
    }
  };

  return (
    <>
      {!flow ? (
        <Center>
          <Loader size="xl" mt="xl" />
        </Center>
      ) : (
        <Stack>
          <form
            onSubmit={form.onSubmit(() => {
              saveVersion();
            }, handleError)}
          >
            {isSingleTemplate ? (
              renderTemplateFields(templates[0], 0, { showDelete: false })
            ) : (
              <Accordion
                mt={0}
                variant="contained"
                value={selectedItem}
                onChange={setSelectedItem}
              >
                {fields}
              </Accordion>
            )}

            {formErrors?.length > 0 && (
              <Paper withBorder p="md" mt="md" radius="md" c="red">
                <Text fw={500}>Validation Errors</Text>
                <Text mb="sm" fz="sm" c="dimmed">
                  Click on each error to reveal the template that causes the
                  issue
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  center
                  icon={
                    <ThemeIcon c="white" color="red.7" size={24} radius="xl">
                      <IconCircleXFilled
                        style={{ width: rem(16), height: rem(16) }}
                      />
                    </ThemeIcon>
                  }
                >
                  {formErrors.map((error) => (
                    <List.Item
                      key={"err-" + error.key}
                      onClick={() => revealErrorAccordion(error.key)}
                      style={{ cursor: "pointer" }}
                    >
                      {error.value}
                    </List.Item>
                  ))}
                </List>
              </Paper>
            )}

            {flow.isEditable && (
              <Group
                mt="md"
                justify="flex-end"
                style={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 10,
                  backgroundColor: "var(--mantine-color-body)",
                  paddingBlock: "var(--mantine-spacing-sm)",
                  borderTop: "1px solid var(--mantine-color-default-border)",
                }}
              >
                <Button
                  variant="outline"
                  color="red"
                  leftSection={<IconTrash size={16} stroke={1.5} />}
                  onClick={() =>
                    modals.openConfirmModal({
                      title: "Delete Version",
                      centered: true,
                      children: (
                        <Text size="sm">
                          Are you sure you want to delete this version? This
                          action cannot be undone.
                        </Text>
                      ),
                      labels: { confirm: "Delete", cancel: "Cancel" },
                      confirmProps: { color: "red" },
                      onCancel: () => console.log("Cancel"),
                      onConfirm: () => {
                        deleteVersion(flow.flowId, flow.id).then(() => {
                          notifications.show({
                            title: "Version deleted",
                            color: "green",
                          });
                          setFlowTemplates(
                            flowTemplates.filter((q) => q.id != flow.id)
                          );
                          modals.closeAll();
                          setSelectedItem(null);
                          setSelectedVersion(flow.flowId, null);
                        });
                      },
                    })
                  }
                >
                  Delete Version
                </Button>

                {!isSingleTemplate && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      form.insertListItem("templates", {
                        key: randomId(),
                        name: "",
                        template: "",
                        verifyProgress: false,
                      })
                    }
                  >
                    Add Template
                  </Button>
                )}
                {isSingleTemplate && (
                  <Tooltip label="Add a sub-template for prompt composition via [[name]] syntax">
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={() =>
                        form.insertListItem("templates", {
                          key: randomId(),
                          name: "",
                          template: "",
                          verifyProgress: false,
                        })
                      }
                    >
                      + Sub-template
                    </Button>
                  </Tooltip>
                )}
                {form.isDirty() && (
                  <Badge color="yellow" variant="light">
                    Unsaved changes
                  </Badge>
                )}
                <Tooltip
                  position="bottom"
                  label="Save all templates"
                  multiline
                  w={320}
                  withArrow
                  openDelay={600}
                  transitionProps={{ duration: 350 }}
                >
                  <Button
                    leftSection={<IconDeviceFloppy stroke={1.2} />}
                    type="submit"
                  >
                    Save
                  </Button>
                </Tooltip>
              </Group>
            )}
          </form>
        </Stack>
      )}

      <LinkAgentModal
        opened={linkAgentModalOpened}
        onClose={linkAgentModal.close}
        onToolCreated={handleAgentToolCreated}
        excludeFlowId={parentFlow?.id}
      />
    </>
  );
});

FlowVersionEditor.displayName = "FlowVersionEditor";

export default FlowVersionEditor;
